import React, { useState, useEffect } from 'react';
import './WalletConnect.css';

const WalletConnect = ({ network, onWalletConnected, onWalletDisconnected }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [address, setAddress] = useState('');
  const [balance, setBalance] = useState('0');
  const [showWalletMenu, setShowWalletMenu] = useState(false);
  const [showWalletModal, setShowWalletModal] = useState(false);

  const fetchBalance = async () => {
    if (!address) return;
    
    try {
      const response = await fetch(network.rpcUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jsonrpc: '2.0',
          method: 'avo_getBalance',
          params: [address],
          id: 1
        })
      });

      const result = await response.json();
      if (result.result && result.result.balance_avo) {
        setBalance(parseFloat(result.result.balance_avo).toFixed(4));
      }
    } catch (error) {
      console.error('Error fetching balance:', error);
    }
  };

  const connectWallet = async (walletAddress) => {
    setAddress(walletAddress);
    setIsConnected(true);
    localStorage.setItem('avo_wallet_address', walletAddress);
    
    if (onWalletConnected) {
      onWalletConnected(walletAddress);
    }

    // Fetch initial balance
    try {
      const response = await fetch(network.rpcUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jsonrpc: '2.0',
          method: 'avo_getBalance',
          params: [walletAddress],
          id: 1
        })
      });

      const result = await response.json();
      if (result.result && result.result.balance_avo) {
        setBalance(parseFloat(result.result.balance_avo).toFixed(4));
      }
    } catch (error) {
      console.error('Error fetching balance:', error);
    }
  };

  // Check if wallet is already connected (from localStorage)
  useEffect(() => {
    const savedAddress = localStorage.getItem('avo_wallet_address');
    if (savedAddress) {
      connectWallet(savedAddress);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update balance when network or address changes
  useEffect(() => {
    if (isConnected && address) {
      fetchBalance();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [network, address, isConnected]);

  const disconnectWallet = () => {
    setIsConnected(false);
    setAddress('');
    setBalance('0');
    setShowWalletMenu(false);
    localStorage.removeItem('avo_wallet_address');
    
    if (onWalletDisconnected) {
      onWalletDisconnected();
    }
  };

  const handleManualConnect = () => {
    setShowWalletModal(true);
  };

  const handleAddressSubmit = (e) => {
    e.preventDefault();
    const inputAddress = e.target.address.value.trim();
    
    if (inputAddress.startsWith('0x') && inputAddress.length === 42) {
      connectWallet(inputAddress);
      setShowWalletModal(false);
    } else {
      alert('Invalid AVO address format. Address must start with 0x and be 42 characters long.');
    }
  };

  const copyAddress = () => {
    navigator.clipboard.writeText(address);
    alert('Address copied to clipboard!');
  };

  const shortenAddress = (addr) => {
    if (!addr) return '';
    return `${addr.substring(0, 6)}...${addr.substring(addr.length - 4)}`;
  };

  return (
    <>
      <div className="wallet-connect">
        {!isConnected ? (
          <button className="connect-button" onClick={handleManualConnect}>
            Connect Wallet
          </button>
        ) : (
          <div className="wallet-info">
            <div className="balance-display">
              {balance} AVO
            </div>
            <button 
              className="address-button"
              onClick={() => setShowWalletMenu(!showWalletMenu)}
            >
              {shortenAddress(address)}
            </button>

            {showWalletMenu && (
              <div className="wallet-menu">
                <div className="wallet-menu-header">
                  <div className="wallet-menu-address">
                    {address}
                    <button className="copy-btn" onClick={copyAddress}>Copy</button>
                  </div>
                </div>
                <div className="wallet-menu-balance">
                  Balance: {balance} AVO
                </div>
                <div className="wallet-menu-actions">
                  <button className="menu-action-btn" onClick={() => {
                    setShowWalletMenu(false);
                    window.location.href = `#/address/${address}`;
                  }}>
                    View Account
                  </button>
                  <button className="menu-action-btn" onClick={() => {
                    setShowWalletMenu(false);
                    alert('Staking interface coming soon!');
                  }}>
                    Stake
                  </button>
                  <button className="menu-action-btn disconnect" onClick={disconnectWallet}>
                    Disconnect
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Wallet Connection Modal */}
      {showWalletModal && (
        <div className="wallet-modal-overlay" onClick={() => setShowWalletModal(false)}>
          <div className="wallet-modal" onClick={(e) => e.stopPropagation()}>
            <div className="wallet-modal-header">
              <h2>Connect Wallet</h2>
              <button className="modal-close" onClick={() => setShowWalletModal(false)}>×</button>
            </div>

            <div className="wallet-modal-content">
              <p className="modal-description">
                Enter your AVO wallet address
              </p>

              <form onSubmit={handleAddressSubmit}>
                <div className="form-group">
                  <label htmlFor="address">Address</label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    placeholder="0x..."
                    className="address-input"
                    autoComplete="off"
                  />
                </div>

                <div className="wallet-options">
                  <div className="wallet-option">
                    <div className="option-info">
                      <strong>Read-Only Mode</strong>
                      <p>Use AVO CLI for transactions</p>
                    </div>
                  </div>
                </div>

                <button type="submit" className="submit-button">
                  Connect
                </button>
              </form>

              <div className="wallet-help">
                <p><strong>Create wallet:</strong></p>
                <code>avo.exe account create</code>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WalletConnect;
